/**
 * Top-level CoverageInfo page component. Fetches coverage, order, and
 * auth-on-file data for a given order ID, fires the EV_SCREEN_VIEWED
 * analytics event, and renders the CoverageDetails view, a blocked-order page,
 * or an access-denied page.
 *
 * @module coverageInfo/coverageInfo
 */
import { useNavigate, useParams } from "react-router-dom";
import AccessDeniedPage from "../../components/accessDeniedPage/accessDeniedPage";
import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import OrderBlocked from "../../components/orderBlocked/orderBlocked";
import { useDataAccumulatorForSingleOrder } from "../../context/dataAccumulatorForSingleOrder";
import { GeneralOrderDetails } from "../../data-model/medicalPaOrdersModel";
import { MedicalOrderStatus } from "../../enums/orderReviewStatus";
import { useOrderAccessPermission } from "../../hooks/useOrderAccessPermission";
import { useOrderBlocked } from "../../hooks/useOrderBlocked";
import { AgentAiIcon } from "../../svg/agent-ai-icon";
import InProgress from "../../svg/inProgress";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { CoverageDetails } from "./coverageDetails/coverageDetails";
import { useCoverageScreenInit } from "./hooks/useCoverageScreenInit";

/**
 * Page-level component for the coverage information screen.
 * Orchestrates data fetching, access permission checks, J-code initialization,
 * and a one-time analytics event before delegating to CoverageDetails.
 *
 * @returns Loading indicator, blocked-order page, access-denied page, or CoverageDetails
 */
const CoverageInfo = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const orgId = getOrgIdForFetchExternalWorklist();

  // Read from the shared, hoisted accumulator so navigating here from the
  // single-order or medical-pa-form screens reuses the already-fetched order
  // data instead of firing a fresh set of coverage API calls.
  const {
    coverageDetails,
    coverageDetailsLoading,
    generalOrderDetails,
    generalOrderDetailsLoading,
    authOnFileDetails,
    authOnFileDetailsLoading,
    submissionData,
    submissionDataLoading,
    fetchAll,
    abortAll,
  } = useDataAccumulatorForSingleOrder();

  // Gate the page loader only on the domains the coverage screen renders.
  const loading =
    coverageDetailsLoading ||
    generalOrderDetailsLoading ||
    authOnFileDetailsLoading ||
    submissionDataLoading;

  // Primes the accumulator for this order and, once the order, auth-on-file,
  // and coverage data have settled, persists J-codes, sets the primary payer
  // name on the layout, and fires the one-time EV_SCREEN_VIEWED event.
  useCoverageScreenInit({
    orderId: id,
    orgId,
    generalOrderDetails,
    authOnFileDetails,
    coverageDetails,
    loading,
    fetchAll,
    abortAll,
  });

  /**
   * Force a fresh fetch of all order domains (coverage, order details,
   * auth-on-file, and submission) after a successful coverage save.
   *
   * @returns Resolves once the full accumulator refetch settles
   */
  const refetchAllData = () => fetchAll(id ?? "", orgId);

  const order = generalOrderDetails?.order
    ? (generalOrderDetails.order as unknown as GeneralOrderDetails)
    : null;

  const { hasAccess, errorMessage: accessErrorMessage } =
    useOrderAccessPermission(order, loading);
  const isOrderBlocked = useOrderBlocked(order, loading);

  const handleOrderBlockedBackClick = () => {
    navigate("/worklists/");
  };

  const medicalOrderStatus = order?.status?.medical_order_status;
  const isAgentInProgressOrder =
    medicalOrderStatus === MedicalOrderStatus.AGENT_IN_PROGRESS;

  return (
    <div className="coverage-info__main-container flex h-full w-full flex-col justify-center gap-1.5 overflow-hidden sm:gap-2.5 md:gap-3">
      {loading ? (
        <LoaderMessage message="Fetching Coverage Details..." />
      ) : !hasAccess ? (
        <AccessDeniedPage message={accessErrorMessage ?? undefined} />
      ) : isOrderBlocked ? (
        <OrderBlocked
          icon={
            isAgentInProgressOrder ? (
              <AgentAiIcon fill="#E65100" height={48} width={48} />
            ) : (
              <InProgress height={48} width={48} />
            )
          }
          header={
            isAgentInProgressOrder
              ? "Agent In Progress"
              : "Order Under Development"
          }
          subheader={
            isAgentInProgressOrder
              ? "This order is being processed by an agent. Please proceed with your other orders for now."
              : "This order is currently in development. Please proceed with your other orders for now."
          }
          buttonText="Go Back to Dashboard"
          onClick={handleOrderBlockedBackClick}
        />
      ) : (
        <>
          <div className="coverage-info--body flex-1 overflow-hidden bg-white">
            <CoverageDetails
              id={id}
              coverageResponse={
                coverageDetails ?? { coverages: [], coverage_results: [] }
              }
              demographicsData={
                generalOrderDetails?.order?.demographics ?? undefined
              }
              payload={generalOrderDetails?.order?.payload ?? undefined}
              status={generalOrderDetails?.order?.status ?? undefined}
              authOnFileDetails={authOnFileDetails?.auth_on_file ?? undefined}
              generalOrderDetails={order ?? undefined}
              submissionData={submissionData?.submission ?? undefined}
              onSuccess={refetchAllData}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default CoverageInfo;
